// src/pages/AnalyticsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, orderBy, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAppData } from '../context/AppDataContext';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import CampaignAssignmentModal from '../components/CampaignAssignmentModal';
import '../styles/AnalyticsPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// --- Metric helpers ---
// Facebook events have 0 invitations. Their response rate is calculated as
// appointments / responses (e.g. 9 appointments on 160 responses = 5.6%).
// Mailers use the scraped rate, falling back to responses / invitations.
const isFacebookEvent = (campaign) => (parseInt(campaign.invitations) || 0) === 0;

const getResponseRate = (campaign) => {
  const responses = parseInt(campaign.responses) || 0;
  const appointments = parseInt(campaign.appointments) || 0;
  const invitations = parseInt(campaign.invitations) || 0;

  if (isFacebookEvent(campaign)) {
    if (responses === 0) return null;
    return (appointments / responses) * 100;
  }

  // Mailer: prefer the scraped rate if present
  if (campaign.responseRate) {
    const parsed = parseFloat(String(campaign.responseRate).replace('%', ''));
    if (!isNaN(parsed)) return parsed;
  }
  if (invitations === 0) return null;
  return (responses / invitations) * 100;
};

const formatRate = (rate) =>
  rate === null || rate === undefined || isNaN(rate) ? 'N/A' : `${rate.toFixed(1)}%`;

function AnalyticsPage() {
  const { clients } = useAppData();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [dateRange, setDateRange] = useState('30'); // days
  // eslint-disable-next-line
  const [viewMode, setViewMode] = useState('current'); // 'current' or 'historical'
  const [chartMode, setChartMode] = useState('incremental'); // 'incremental' or 'cumulative'
  const [assignmentModal, setAssignmentModal] = useState({
    isOpen: false,
    record: null
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [campaignTypeFilter, setCampaignTypeFilter] = useState('all'); // 'all', 'facebook', 'mailer'
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [manufacturerFilter, setManufacturerFilter] = useState('all');

  
  
  // Fetch analytics data - wrapped in useCallback
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      let analyticsQuery = query(
        collection(db, 'analytics'),
        orderBy('scrapedAt', 'desc')
      );
      
      // Filter by date range
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));
      analyticsQuery = query(
        analyticsQuery,
        where('scrapedAt', '>=', Timestamp.fromDate(daysAgo))
      );
      
      // Filter by client if selected
      if (selectedClient !== 'all') {
        analyticsQuery = query(
          analyticsQuery,
          where('clientId', '==', selectedClient)
        );
      }
      
      const snapshot = await getDocs(analyticsQuery);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scrapedAt: doc.data().scrapedAt?.toDate()
      }));
      
      setAnalyticsData(data);
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedClient, dateRange]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  const openAssignmentModal = (record) => {
    setAssignmentModal({
      isOpen: true,
      record
    });
  };
  
  const closeAssignmentModal = () => {
    setAssignmentModal({
      isOpen: false,
      record: null
    });
    // Refresh data after assignment
    fetchAnalytics();
  };
  
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  const getFilteredCampaigns = (campaigns) => {
    let filtered = campaigns;
    
    // Filter by campaign type
    if (campaignTypeFilter !== 'all') {
      filtered = filtered.filter(campaign => {
        const invitations = parseInt(campaign.invitations) || 0;
        if (campaignTypeFilter === 'facebook') {
          return invitations === 0; // Facebook events have 0 invitations
        } else if (campaignTypeFilter === 'mailer') {
          return invitations > 0; // Mailers have invitations
        }
        return true;
      });
    }
    
    // Filter by state - match against client's state
    if (stateFilter !== 'all') {
      filtered = filtered.filter(campaign => {
        const client = clients.find(c => c.id === campaign.clientId);
        return client && client.state === stateFilter;
      });
    }
    
    // Filter by manufacturer - match against client's manufacturer
    if (manufacturerFilter !== 'all') {
      filtered = filtered.filter(campaign => {
        const client = clients.find(c => c.id === campaign.clientId);
        return client && client.manufacturer === manufacturerFilter;
      });
    }
    
    // Filter by date range based on sale dates
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));
    daysAgo.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    filtered = filtered.filter(campaign => {
      // If no sale dates, include the campaign (it might be ongoing)
      if (!campaign.saleDates || campaign.saleDates.length === 0) {
        return true;
      }
      
      // Check if any sale date falls within the selected date range
      return campaign.saleDates.some(dateStr => {
        // Handle formats like "02-19-2025 thru 02-28-2025" or "11-15-2024 thru 11-23-2024"
        // Split by "thru" or "to" to get date ranges
        const dateRangeParts = dateStr.split(/\s+(?:thru|to)\s+/i);
        
        // Parse all dates in the range
        const datesToCheck = dateRangeParts.flatMap(part => {
          // Split by comma in case there are multiple date ranges
          return part.split(',').map(d => d.trim());
        });
        
        return datesToCheck.some(datePart => {
          // Remove any extra whitespace
          datePart = datePart.trim();
          
          // Try to parse MM-DD-YYYY format
          const parts = datePart.split('-');
          if (parts.length === 3) {
            const month = parseInt(parts[0]) - 1; // Month is 0-indexed
            const day = parseInt(parts[1]);
            const year = parseInt(parts[2]);
            
            if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
              const saleDate = new Date(year, month, day);
              return saleDate >= daysAgo && saleDate <= today;
            }
          }
          
          // If parsing fails, include it to be safe
          return true;
        });
      });
    });
    
    // Filter by search term (campaign title)
    if (searchTerm.trim()) {
      filtered = filtered.filter(campaign => 
        campaign.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };
  
  const getSortedCampaigns = (campaigns) => {
    // First filter by campaign type
    const filteredCampaigns = getFilteredCampaigns(campaigns);
    
    // Then apply sorting
    if (!sortConfig.key) return filteredCampaigns;
    
    return [...filteredCampaigns].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'client':
          aValue = a.clientName?.toLowerCase() || '';
          bValue = b.clientName?.toLowerCase() || '';
          break;
        case 'campaign':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'invitations':
          aValue = parseInt(a.invitations) || 0;
          bValue = parseInt(b.invitations) || 0;
          break;
        case 'responses':
          aValue = parseInt(a.responses) || 0;
          bValue = parseInt(b.responses) || 0;
          break;
        case 'appointments':
          aValue = parseInt(a.appointments) || 0;
          bValue = parseInt(b.appointments) || 0;
          break;
        case 'responseRate':
          aValue = getResponseRate(a) ?? -1;
          bValue = getResponseRate(b) ?? -1;
          break;
        case 'type':
          aValue = isFacebookEvent(a) ? 1 : 0;
          bValue = isFacebookEvent(b) ? 1 : 0;
          break;
        case 'status':
          aValue = a.isAssigned ? 1 : 0;
          bValue = b.isAssigned ? 1 : 0;
          break;
        case 'saleDates':
          // Sort by first sale date if available
          aValue = a.saleDates && a.saleDates.length > 0 ? a.saleDates[0] : '';
          bValue = b.saleDates && b.saleDates.length > 0 ? b.saleDates[0] : '';
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  // Get current active campaigns (most recent scrape for each client)
  const getCurrentCampaigns = useMemo(() => {
    const latestByClient = {};
    
    analyticsData.forEach(record => {
      if (!latestByClient[record.clientId] || 
          record.scrapedAt > latestByClient[record.clientId].scrapedAt) {
        latestByClient[record.clientId] = record;
      }
    });
    
    const campaigns = [];
    Object.values(latestByClient).forEach(record => {
      record.campaigns?.forEach(campaign => {
        // Check if this campaign is assigned
        const isAssigned = record.assignments && record.assignments[campaign.title];
        campaigns.push({
          ...campaign,
          clientName: record.clientName,
          clientId: record.clientId,
          scrapedAt: record.scrapedAt,
          recordId: record.id,
          isAssigned,
          assignedOrderId: isAssigned ? record.assignments[campaign.title] : null
        });
      });
    });
    
    return campaigns;
  }, [analyticsData]);
  
  // Prepare chart data for trends over time - FIXED VERSION
  const getTrendChartData = useMemo(() => {
  if (selectedClient === 'all') return null;
  
  // Get client data and FORCE chronological order (oldest first)
  const clientData = analyticsData
    .filter(d => d.clientId === selectedClient)
    .sort((a, b) => {
      const timeA = a.scrapedAt.getTime();
      const timeB = b.scrapedAt.getTime();
      return timeA - timeB; // Ascending (oldest to newest)
    });
  
  if (clientData.length === 0) return null;
  
  
  // Get all unique dates
  const uniqueDates = [...new Set(clientData.map(record => 
  record.scrapedAt.toLocaleDateString()
))].sort((a, b) => {
  return new Date(a) - new Date(b); // Sort chronologically
});
  
  
  // Track each campaign's progression over time
  const campaignTrends = {};
  
  // Process records in chronological order
  clientData.forEach((record) => {
    const dateStr = record.scrapedAt.toLocaleDateString();
    
    record.campaigns?.forEach(campaign => {
      const campaignTitle = campaign.title;
      const currentTotal = parseInt(campaign.responses) || 0;
      
      // Initialize if first time seeing this campaign
      if (!campaignTrends[campaignTitle]) {
        campaignTrends[campaignTitle] = [];
      }
      
      const campaignHistory = campaignTrends[campaignTitle];
      const lastEntry = campaignHistory.length > 0 
        ? campaignHistory[campaignHistory.length - 1]
        : null;
      
      let incremental = 0;
      
      if (!lastEntry) {
        // First appearance: baseline is current value, show 0 new
        incremental = 0;
      } else {
        // Calculate change from last scrape
        const previousTotal = lastEntry.cumulative;
        const change = currentTotal - previousTotal;
        
        // Responses should only increase (or stay same)
        if (change < 0) {
          console.warn(`[${campaignTitle}] NEGATIVE CHANGE on ${dateStr}: ${previousTotal} → ${currentTotal} (${change})`);
          incremental = 0; // Treat as no new responses
        } else {
          incremental = change;
          console.log(`[${campaignTitle}] ${dateStr}: ${previousTotal} → ${currentTotal} (+${incremental})`);
        }
      }
      
      // Store this data point
      campaignHistory.push({
        date: dateStr,
        incremental: incremental,
        cumulative: currentTotal
      });
    });
  });
  
  // Build chart datasets
  const validCampaigns = Object.entries(campaignTrends)
    .filter(([_, dataPoints]) => dataPoints.length > 0);
  
  if (validCampaigns.length === 0) {
    console.log('No valid campaigns with data');
    return null;
  }
  
  console.log(`Creating datasets for ${validCampaigns.length} campaigns`);
  
  const datasets = validCampaigns.map(([campaignTitle, dataPoints], index) => {
    // Create lookup for quick access
    const dataByDate = new Map(
      dataPoints.map(dp => [
        dp.date,
        chartMode === 'cumulative' ? dp.cumulative : dp.incremental
      ])
    );
    
    // Fill in values for all dates (null where campaign didn't exist)
    const values = uniqueDates.map(date => {
      const val = dataByDate.get(date);
      return val !== undefined ? val : null;
    });
        
    const hue = (index * 360) / validCampaigns.length;

    return {
      label: campaignTitle,
      data: values,
      borderColor: `hsl(${hue}, 70%, 50%)`,
      backgroundColor: `hsla(${hue}, 70%, 50%, 0.1)`,
      tension: 0.4,
      spanGaps: true,
      fill: false,
      pointRadius: 4,
      pointHoverRadius: 6
    };
  });
  
  console.log('RETURNING TREND DATA:', {
    labelCount: uniqueDates.length,
    datasetCount: datasets.length,
    labels: uniqueDates,
    firstDataset: datasets[0]
  });
  
  return {
    labels: uniqueDates,
    datasets
  };
}, [analyticsData, selectedClient, chartMode]);
  
  // Build performance chart data for a given set of campaigns (already filtered).
  // Sorts by computed response rate and takes the top 7.
  const buildPerformanceChartData = (campaigns) => {
    const topCampaigns = [...campaigns]
      .map(c => ({ ...c, computedRate: getResponseRate(c) }))
      .filter(c => c.computedRate !== null)
      .sort((a, b) => b.computedRate - a.computedRate)
      .slice(0, 7);
    
    if (topCampaigns.length === 0) return null;
    
    return {
      labels: topCampaigns.map(c => 
        selectedClient === 'all' 
          ? [c.clientName, c.title]  // Array creates multi-line label
          : c.title
      ),
      datasets: [
        {
          label: 'Response Rate (%)',
          data: topCampaigns.map(c => Number(c.computedRate.toFixed(2))),
          backgroundColor: topCampaigns.map(c =>
            isFacebookEvent(c) ? 'rgba(24, 119, 242, 0.5)' : 'rgba(1, 90, 255, 0.5)'
          ),
          borderColor: 'rgba(25, 0, 255, 1)',
          borderWidth: 1,
        }
      ],
      // Extra metadata for tooltips (ignored by Chart.js rendering)
      meta: topCampaigns.map(c => ({
        type: isFacebookEvent(c) ? 'Facebook Event' : 'Mailer',
        responses: parseInt(c.responses) || 0,
        appointments: parseInt(c.appointments) || 0,
        invitations: parseInt(c.invitations) || 0
      }))
    };
  };
  
  const currentCampaigns = getCurrentCampaigns;
  const trendData = getTrendChartData;
  
  // Extract unique states and manufacturers from clients
  const uniqueStates = useMemo(() => {
    const states = new Set();
    clients.forEach(client => {
      if (client.state) {
        states.add(client.state);
      }
    });
    return Array.from(states).sort();
  }, [clients]);
  
  const uniqueManufacturers = useMemo(() => {
    const manufacturers = new Set();
    clients.forEach(client => {
      if (client.manufacturer) {
        manufacturers.add(client.manufacturer);
      }
    });
    return Array.from(manufacturers).sort();
  }, [clients]);
  
  // Alphabetized client list for the dropdown
  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [clients]);
  
  // Apply campaign type filter to current campaigns
  const filteredCurrentCampaigns = getFilteredCampaigns(currentCampaigns);
  
  // Summary stats (computed from the filtered set so cards always match the table)
  const fbCampaigns = filteredCurrentCampaigns.filter(isFacebookEvent);
  const mailerCampaigns = filteredCurrentCampaigns.filter(c => !isFacebookEvent(c));
  
  // One performance chart per campaign type (respects all active filters)
  const mailerPerformanceData = buildPerformanceChartData(mailerCampaigns);
  const fbPerformanceData = buildPerformanceChartData(fbCampaigns);
  const totalResponses = filteredCurrentCampaigns.reduce((sum, c) => sum + (parseInt(c.responses) || 0), 0);
  const totalAppointments = filteredCurrentCampaigns.reduce((sum, c) => sum + (parseInt(c.appointments) || 0), 0);
  const mailerInvitations = mailerCampaigns.reduce((sum, c) => sum + (parseInt(c.invitations) || 0), 0);
  const mailerResponses = mailerCampaigns.reduce((sum, c) => sum + (parseInt(c.responses) || 0), 0);
  // Weighted rates (totals-based, not an average of percentages)
  const mailerResponseRate = mailerInvitations > 0 ? (mailerResponses / mailerInvitations) * 100 : null;
  const appointmentConversion = totalResponses > 0 ? (totalAppointments / totalResponses) * 100 : null;
  
  // Shared Bar chart options; tooltip pulls calculation detail from chartData.meta
  const makeBarOptions = (chartData) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            const m = chartData.meta?.[context.dataIndex];
            if (!m) return '';
            return m.type === 'Facebook Event'
              ? `${m.appointments} appts / ${m.responses} responses`
              : `${m.responses} responses / ${m.invitations.toLocaleString()} invitations`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Response Rate (%)'
        },
        ticks: {
          precision: 2,
          callback: function(value) {
            return value + '%';
          }
        }
      }
    },
    animation: false,
    onResize: null
  });
  
  if (loading) {
    return <div className="analytics-container"><p>Loading analytics...</p></div>;
  }
  
  return (
    <div className="analytics-container">
      <div className="client-header">
        <h2 className="gradient-text" style={{ margin: '0 0 44px 0', alignItems: 'center', display: 'flex', flexDirection: 'column'}}>
          Analytics Dashboard
        </h2>
        <div className="client-actions">
          <select 
            value={selectedClient} 
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <option value="all">All Clients</option>
            {sortedClients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
          
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 6 months</option>
            <option value="365">Last year</option>
          </select>
          
          <select 
            value={campaignTypeFilter} 
            onChange={(e) => setCampaignTypeFilter(e.target.value)}
          >
            <option value="all">All Campaigns</option>
            <option value="mailer">Mailers Only</option>
            <option value="facebook">Facebook Events Only</option>
          </select>
          
          <select 
            value={stateFilter} 
            onChange={(e) => setStateFilter(e.target.value)}
          >
            <option value="all">All States</option>
            {uniqueStates.map(state => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          
          <select 
            value={manufacturerFilter} 
            onChange={(e) => setManufacturerFilter(e.target.value)}
          >
            <option value="all">All Manufacturers</option>
            {uniqueManufacturers.map(manufacturer => (
              <option key={manufacturer} value={manufacturer}>
                {manufacturer}
              </option>
            ))}
          </select>
          
          {(selectedClient !== 'all' || campaignTypeFilter !== 'all' || stateFilter !== 'all' || manufacturerFilter !== 'all' || searchTerm.trim()) && (
            <button
              onClick={() => {
                setSelectedClient('all');
                setCampaignTypeFilter('all');
                setStateFilter('all');
                setManufacturerFilter('all');
                setSearchTerm('');
              }}
              style={{
                padding: '6px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Clear Filters
            </button>
          )}
          
          {viewMode === 'historical' && selectedClient !== 'all' && (
            <div className="chart-mode-toggle">
              <button
                className={chartMode === 'incremental' ? 'active' : ''}
                onClick={() => setChartMode('incremental')}
                title="Show new responses per period"
              >
                Incremental
              </button>
              <button
                className={chartMode === 'cumulative' ? 'active' : ''}
                onClick={() => setChartMode('cumulative')}
                title="Show total cumulative responses"
              >
                Cumulative
              </button>
            </div>
          )}
        </div>

<div className="orders-search-container">
                  <input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '200px'
            }}
          />
          </div>
          
      </div>
      
      {viewMode === 'current' && (
        <>
          <div className="stats-overview">
            <div className="stat-card">
              <h3>Active Campaigns</h3>
              <p className="stat-number">{filteredCurrentCampaigns.length}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
                {mailerCampaigns.length} mailers · {fbCampaigns.length} Facebook
              </p>
            </div>
            <div className="stat-card">
              <h3>Total Responses</h3>
              <p className="stat-number">{totalResponses}</p>
            </div>
            <div className="stat-card">
              <h3>Total Appointments</h3>
              <p className="stat-number">{totalAppointments}</p>
            </div>
            <div className="stat-card" title="Total mailer responses ÷ total invitations (weighted, mailers only)">
              <h3>Mailer Response Rate</h3>
              <p className="stat-number">{formatRate(mailerResponseRate)}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
                {mailerResponses} / {mailerInvitations.toLocaleString()} invitations
              </p>
            </div>
            <div className="stat-card" title="Total appointments ÷ total responses across all campaigns">
              <h3>Appt Conversion</h3>
              <p className="stat-number">{formatRate(appointmentConversion)}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
                {totalAppointments} / {totalResponses} responses
              </p>
            </div>
          </div>
          
          {campaignTypeFilter !== 'facebook' && (
          <div className="chart-section">
            <h2>Top Performing Mailers</h2>
            {mailerPerformanceData ? (
              <div style={{ height: '300px', position: 'relative' }}>
                <Bar 
                  key={`bar-mailer-${selectedClient}-${dateRange}-${stateFilter}-${manufacturerFilter}-${searchTerm}`}
                  data={mailerPerformanceData}
                  options={makeBarOptions(mailerPerformanceData)}
                />
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
                No mailer campaign data available to display
              </div>
            )}
          </div>
          )}
          
          {campaignTypeFilter !== 'mailer' && (
          <div className="chart-section">
            <h2>Top Performing Facebook Events</h2>
            {fbPerformanceData ? (
              <div style={{ height: '300px', position: 'relative' }}>
                <Bar 
                  key={`bar-fb-${selectedClient}-${dateRange}-${stateFilter}-${manufacturerFilter}-${searchTerm}`}
                  data={fbPerformanceData}
                  options={makeBarOptions(fbPerformanceData)}
                />
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
                No Facebook event data available to display
              </div>
            )}
          </div>
          )}
          
          <div className="campaigns-table">
            <h2>
              Current Active Campaigns{' '}
              <span style={{ fontSize: '14px', fontWeight: 400, color: '#6c757d' }}>
                ({getSortedCampaigns(currentCampaigns).length} shown)
              </span>
            </h2>
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('client')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Client {sortConfig.key === 'client' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th onClick={() => handleSort('campaign')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Campaign {sortConfig.key === 'campaign' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th onClick={() => handleSort('type')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Type {sortConfig.key === 'type' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th onClick={() => handleSort('invitations')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Invitations {sortConfig.key === 'invitations' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th onClick={() => handleSort('responses')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Responses {sortConfig.key === 'responses' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th onClick={() => handleSort('appointments')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Appointments {sortConfig.key === 'appointments' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th onClick={() => handleSort('responseRate')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Response Rate {sortConfig.key === 'responseRate' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th onClick={() => handleSort('saleDates')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Sale Dates {sortConfig.key === 'saleDates' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Status {sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSortedCampaigns(currentCampaigns).map((campaign, index) => {
                  const fb = isFacebookEvent(campaign);
                  const rate = getResponseRate(campaign);
                  return (
                  <tr key={index}>
                    <td>{campaign.clientName}</td>
                    <td>{campaign.title}</td>
                    <td>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        background: fb ? '#e7f0ff' : '#e9f7ef',
                        color: fb ? '#1877f2' : '#1e7e34'
                      }}>
                        {fb ? 'Facebook' : 'Mailer'}
                      </span>
                    </td>
                    <td>{fb ? '-' : campaign.invitations}</td>
                    <td>{campaign.responses}</td>
                    <td>{campaign.appointments}</td>
                    <td title={fb
                      ? `${campaign.appointments || 0} appointments ÷ ${campaign.responses || 0} responses`
                      : `${campaign.responses || 0} responses ÷ ${campaign.invitations || 0} invitations`}>
                      {formatRate(rate)}
                    </td>
                    <td>{campaign.saleDates?.join(' thru ') || 'N/A'}</td>
                    <td>
                      {campaign.isAssigned ? (
                        <span className="badge badge-success">Assigned</span>
                      ) : (
                        <span className="badge badge-warning">Unassigned</span>
                      )}
                    </td>
                    <td>
                      {/* Only show Assign button for mailers (invitations > 0), not Facebook events */}
                      {parseInt(campaign.invitations) > 0 && (
                        <button
                          onClick={() => {
                            const record = analyticsData.find(r => r.id === campaign.recordId);
                            openAssignmentModal(record);
                          }}
                          className="assign-btn"
                        >
                          Assign
                        </button>
                      )}
                      {campaign.leadUrl && (
                        <a href={campaign.leadUrl} target="_blank" rel="noopener noreferrer">
                          Leads
                        </a>
                      )}
                      {' '}
                      {campaign.reportUrl && (
                        <a href={campaign.reportUrl} target="_blank" rel="noopener noreferrer">
                          Report
                        </a>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      
      {viewMode === 'historical' && selectedClient !== 'all' && (
        <div className="chart-section">
          <h2>Campaign Trends Over Time</h2>
          {trendData && trendData.datasets && trendData.datasets.length > 0 && trendData.labels && trendData.labels.length > 0 ? (
            <Line 
              key={`line-${selectedClient}-${dateRange}-${chartMode}`}
              data={trendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  mode: 'index',
                  intersect: false,
                },
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: true,
                    text: chartMode === 'cumulative' 
                      ? 'Cumulative Response Totals Over Time'
                      : 'Incremental Response Trends (New Responses Per Period)'
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y;
                        if (value === null) return null;
                        return `${label}: ${value} ${chartMode === 'cumulative' ? 'total' : 'new'} responses`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    display: true,
                    title: {
                      display: true,
                      text: 'Date'
                    }
                  },
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    beginAtZero: true,
                    reverse: false,
                    min: 0,
                    title: {
                      display: true,
                      text: chartMode === 'cumulative' ? 'Total Responses' : 'New Responses'
                    },
                    ticks: {
                      callback: function(value) {
                        return Math.round(value);
                      }
                    }
                  }
                },
                animation: {
                  duration: 0
                }
              }}
              height={400}
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
              {selectedClient === 'all' 
                ? 'Please select a specific client to view trends'
                : 'No historical data available for this client'}
            </div>
          )}
        </div>
      )}
      
      {viewMode === 'historical' && (
        <div className="historical-table">
          <h2>Historical Analytics Records</h2>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Campaigns</th>
                <th>Total Responses</th>
                <th>Total Appointments</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {analyticsData.map((record) => (
                <tr key={record.id}>
                  <td>{record.scrapedAt?.toLocaleDateString()}</td>
                  <td>{record.clientName}</td>
                  <td>{record.campaigns?.length || 0}</td>
                  <td>
                    {record.campaigns?.reduce((sum, c) => sum + (parseInt(c.responses) || 0), 0)}
                  </td>
                  <td>
                    {record.campaigns?.reduce((sum, c) => sum + (parseInt(c.appointments) || 0), 0)}
                  </td>
                  <td>
                    <button
                      onClick={() => openAssignmentModal(record)}
                      className="assign-btn"
                    >
                      Assign Campaigns
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <CampaignAssignmentModal
        isOpen={assignmentModal.isOpen}
        onClose={closeAssignmentModal}
        analyticsRecord={assignmentModal.record}
      />
    </div>
  );
}

export default AnalyticsPage;